
import { navigate } from 'gatsby';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex } from 'theme-ui';
import DropdownIcon from '@src/assets/icons/dropdown.svg';
import EnIcon from '@src/assets/icons/flag_en.svg';
import LtIcon from '@src/assets/icons/flag_lt.svg';
import RoIcon from '@src/assets/icons/flag_ro.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Dropdown, Option } from '@components/Dropdown';

import { Locale } from '@src/utils/locale';

type LanguageSwitcherProps = {
  languages: {
    locale: Locale;
    slug: string;
    text: string;
  }[];
  pageLocale: string;
  variant?: 'flags' | 'empty';
};

const flagMap = {
  [Locale.en]: <EnIcon className="language-switcher__flag" />,
  [Locale.lt]: <LtIcon className="language-switcher__flag" />,
  // [Locale.ro]: <RoIcon className="language-switcher__flag" />,
};

const tldMap = {
  [Locale.en]: '.com',
  [Locale.lt]: '.lt',
  // [Locale.ro]: '.ro',
};

const resolveDomain = (locale: Locale) => {
  return tldMap[locale];
};

const resolveFlag = (locale?: Locale) => {
  return locale ? flagMap[locale] : undefined;
};

const LanguageSwitcher = ({
  pageLocale,
  languages,
  variant,
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const locale = pageLocale || i18n.language;

  const selectedLanguage = languages.find((lang) => lang.locale === locale);
  const otherLanguages = languages.filter((lang) => lang.locale !== locale);

  const onLinkClick = (slug: string, nextLocale: Locale) => {
    // do tld check and redirect with tld if nextLocale is LT or RO
    // example: mokosi.lt/puslapis click on ro language should redirect to mokosi.ro/pagina
    // but mokosi.lt/puslapis click on en language should redirect to mokosi.lt/page
    // or mokosi.ro/pagina click on en language should redirect to mokosi.ro/page

    const tldThatDoRedirects = ['lt', 'ro'];

    if (typeof window !== `undefined`) {
      const nextDomain = window.location.origin;

      const currentTld = (
        Object.keys(tldMap) as Array<keyof typeof tldMap>
      ).find((key) => nextDomain.includes(resolveDomain(key)));

      if (
        currentTld &&
        currentTld !== nextLocale &&
        tldThatDoRedirects.includes(nextLocale)
      ) {
        window.location.replace(
          `${nextDomain.replace(
            resolveDomain(currentTld),
            resolveDomain(nextLocale)
          )}/${slug === nextLocale ? '' : slug}${window.location.search}`
        );
      } else {
        i18n.changeLanguage(nextLocale);
        navigate(`/${slug}${window.location.search}`);
      }
    }
  };

  if (variant === 'flags') {
    return (
      <Box
        sx={{
          '.dropdown__list-item': {
            padding: 0,
          },
        }}
      >
        <Dropdown
          customToggle={
            <Flex
              sx={{
                alignItems: 'center',
                padding: '10px',
                gap: '10px',
              }}
            >
              {resolveFlag(selectedLanguage?.locale)}
              <DropdownIcon className="drop-icon" />
            </Flex>
          }
          listClassName="navbar__language-toggle"
        >
          {otherLanguages.map(({ slug, locale }) => {
            return (
              <Option key={locale}>
                <Button
                  aria-label={`Switch language to ${locale}`}
                  variant="empty"
                  sx={{
                    display: 'block',
                    color: 'text',
                    textDecoration: 'none',
                    padding: '10px',
                  }}
                  onClick={() => onLinkClick(slug, locale)}
                >
                  {flagMap[locale]}
                </Button>
              </Option>
            );
          })}
        </Dropdown>
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          borderColor: 'fieldBorder',
          borderWidth: '2px',
          borderStyle: 'solid',
          borderRadius: '10px',
          '.dropdown__frame': {
            padding: '10px 20px',
            marginBottom: 0,
            borderRadius: '10px',
          },
          '.dropdown__list': {
            padding: 0,
          },
          '.dropdown__list-item': {
            padding: 0,
          },
        }}
      >
        <Dropdown placeholder={selectedLanguage?.text}>
          {otherLanguages.map(({ text, slug, locale }) => {
            return (
              <Option key={slug + locale}>
                <Button
                  variant="empty"
                  sx={{
                    display: 'block',
                    color: 'text',
                    textDecoration: 'none',
                    padding: '20px',
                    '&:hover': {
                      color: 'primary',
                    },
                  }}
                  onClick={() => onLinkClick(slug, locale)}
                >
                  {text}
                </Button>
              </Option>
            );
          })}
        </Dropdown>
      </Box>
    );
  }
};

export default LanguageSwitcher;
